import React from "react";
import { dayjs, getParticipantByIndex, sharedContext } from "../utils";
import { client } from "../api/client";
import { fieldErrorsFromResponse, mergeAnswers } from "../utils/precheckinAnswers";

// TODO: align this with the backend, used for optimistic updates only
const checkedInStatus = 'Pre-Checked-in';

// CP-6549: the session used to work from the booking login returned, so a party member who joined
// meanwhile (guest app, dashboard) stayed hidden until the next sign-in. The booking is re-read on
// page load, when the tab comes back into view, and once a minute while it stays in view.
const BOOKING_REFRESH_INTERVAL_MS = 60 * 1000;
// focus and visibilitychange usually fire together
const BOOKING_REFRESH_MIN_GAP_MS = 2000;

function isBlankForm(formData) {
    return !formData || Object.values(formData).every((v) => (
        v === '' || v === false || v === null || v === undefined || (Array.isArray(v) && v.length === 0)
    ));
}

const USER_ERROR_MAX_LEN = 220;

function truncateOneLine(text, maxLen) {
    const s = String(text).replace(/\s+/g, ' ').trim();
    if (!s) return '';
    if (s.length <= maxLen) return s;
    return `${s.slice(0, maxLen - 1)}…`;
}

function sanitizeForUserMessage(raw) {
    const s = typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
    if (!s) return '';
    if (/duplicate key value violates unique constraint|auth_user_username_key|IntegrityError/i.test(s)) {
        return 'This email address is already in use. Please use a different one.';
    }
    if (/ProgrammingError|OperationalError|Traceback \(most recent call last\)|relation ".*" does not exist/i.test(s)) {
        return '';
    }
    return s;
}

function extractReadableErrorFromPayload(payload) {
    if (payload == null) return '';
    if (typeof payload !== 'object') return '';
    if (Array.isArray(payload)) {
        const strings = payload.filter((x) => typeof x === 'string');
        return strings.length ? strings.join(' ').trim() : '';
    }

    if (typeof payload.detail === 'string' && payload.detail.trim()) return payload.detail.trim();
    if (Array.isArray(payload.detail) && payload.detail.length) {
        const joined = payload.detail
            .map((d) => (typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.string === 'string' ? d.string : '')))
            .filter(Boolean)
            .join(' ')
            .trim();
        if (joined) return joined;
    }

    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error.trim();
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();

    if (Array.isArray(payload.errors)) {
        const parts = [];
        for (const item of payload.errors) {
            if (typeof item === 'string') parts.push(item);
            else if (item && typeof item === 'object') {
                if (typeof item.message === 'string') parts.push(item.message);
                else if (Array.isArray(item.message)) {
                    parts.push(...item.message.filter((x) => typeof x === 'string'));
                }
            }
        }
        const joined = parts.join(' ').trim();
        if (joined) return joined;
    }

    if (payload.errors && typeof payload.errors === 'object' && !Array.isArray(payload.errors)) {
        const parts = [];
        for (const v of Object.values(payload.errors)) {
            if (typeof v === 'string') parts.push(v);
            else if (Array.isArray(v)) parts.push(...v.filter((x) => typeof x === 'string'));
        }
        const joined = parts.join(' ').trim();
        if (joined) return joined;
    }

    const reserved = new Set(['detail', 'errors', 'error', 'message']);
    for (const [k, v] of Object.entries(payload)) {
        if (reserved.has(k)) continue;
        if (Array.isArray(v)) {
            const strings = v.filter((x) => typeof x === 'string');
            if (strings.length) return strings.join(' ');
        } else if (typeof v === 'string' && v.trim()) return v.trim();
    }

    return '';
}

function statusFallbackMessage(status, actionFallback) {
    if (status === 401) return 'Session expired. Please sign in again.';
    if (status === 403) return 'You are not allowed to do this. Please refresh or sign in again.';
    if (status === 404) return 'Data was not found. Please refresh the page.';
    if (status === 408 || status === 504) return 'The request timed out. Please try again.';
    if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (status >= 500) return 'Something went wrong on our side. Please try again in a few minutes.';
    if (status >= 400) return actionFallback;
    return actionFallback || 'Something went wrong. Please try again.';
}

function userFacingApiError(res, actionFallback) {
    const status = Number(res?.status) || 0;
    try {
        const payload = res?.error?.data;
        let msg = extractReadableErrorFromPayload(payload);
        if (msg) {
            msg = sanitizeForUserMessage(msg);
            msg = truncateOneLine(msg, USER_ERROR_MAX_LEN);
        }
        if (msg) return msg;
    } catch (_) {
        // Unexpected payload shape; fall through to status-based message
    }
    return statusFallbackMessage(status, actionFallback);
}

// A closed tenant (backend tenant retirement): the login and every signed-in call answer
// 401 {code: 'tenant_closed', detail: 'This service is no longer available.'}. Neutral words, never the operator's.
export const TENANT_CLOSED_TEXT = 'This service is no longer available.';

/** The words for a closed tenant's answer (a `client` result), or null for any other answer. */
export function tenantClosedMessage(res) {
    if (Number(res?.status) !== 401) return null;
    const payload = res?.error?.data;
    if (!payload || payload.code !== 'tenant_closed') return null;
    return (typeof payload.detail === 'string' && payload.detail.trim()) || TENANT_CLOSED_TEXT;
}

function applySaveParticipantOptimisticUpdate(booking, payload) {
    const bookingId = payload?.booking_id;
    const userPayload = payload?.user || {};
    const answersPayload = payload?.answers;
    const virtualIndex = payload?._virtualIndex;
    const isVirtualParticipant = bookingId === null && virtualIndex !== undefined;
    const isLeadBookerSave = booking.id === bookingId;

    // Custom-field answers merge into the participant's precheckin_answers ('' clears one)
    const withAnswers = (participant) => (
        answersPayload && typeof answersPayload === 'object'
            ? { precheckin_answers: mergeAnswers(participant.precheckin_answers, answersPayload) }
            : {}
    );

    if (isLeadBookerSave) {
        return {
            ...booking,
            saved: true,
            user: { ...booking.user, ...userPayload },
            ...withAnswers(booking),
        };
    }

    return {
        ...booking,
        party: (booking.party || []).map((p) => {
            const isMatch = isVirtualParticipant
                ? (p.id === null && p._virtualIndex === virtualIndex)
                : (p.id === bookingId);

            if (!isMatch) {
                return p;
            }

            return {
                ...p,
                saved: true,
                user: { ...(p.user || {}), ...userPayload },
                ...withAnswers(p),
            };
        }),
    };
}

// TODO align this with the backend
function alignBookingData(booking) {
    if (!booking) return null;

    // Helper function to map user fields to participant level
    const mapUserFieldsToParticipant = (participant) => {
        if (!participant || !participant.user) return participant;

        return {
            ...participant,
            // Map user fields to participant level for consistency
            passport_number: participant.user.passport_number || participant.passport_number || '',
            emergency_contact: participant.user.emergency_contact || participant.emergency_contact || '',
            custom_note: participant.user.custom_note || participant.custom_note || '',
            is_minor: participant.user.is_minor !== undefined ? participant.user.is_minor : (participant.is_minor || false),
            dietary: participant.user.dietary || participant.dietary || [],
            allergies: participant.user.allergies || participant.allergies || [],
            notes: participant.user.notes || participant.notes || ''
        };
    };

    // Helper function to create tags from tag category fields
    const createTagsFromCategoryFields = (booking) => {
        let tags = [...(booking.tags || [])];

        // Get pre_check_settings with field definitions
        const preCheckSettings = booking.pre_check_settings;
        if (preCheckSettings) {
            // Check both lead_booker_fields and party_member_fields for tag_category types
            const leadBookerFields = preCheckSettings.lead_booker_fields || [];
            const tagCategoryFields = leadBookerFields.filter(field => field.type === 'tag_category');

            // For each tag category field, check if there are corresponding values in booking
            tagCategoryFields.forEach(field => {
                const categoryCode = field.category_id || field.id;

                if (categoryCode && booking[categoryCode]) {
                    const tagIds = Array.isArray(booking[categoryCode]) ? booking[categoryCode] : [booking[categoryCode]];

                    tagIds.forEach(tagId => {
                        // Check if tag already exists
                        const existingTag = tags.find(tag => tag.id === tagId);
                        if (!existingTag) {
                            // Find the tag option from field definition
                            const tagOption = field.options?.find(option => option.id === tagId);
                            if (tagOption) {
                                // Create a proper tag structure
                                tags.push({
                                    id: tagId,
                                    name: tagOption.name,
                                    category: { code: categoryCode }
                                });
                            }
                        }
                    });
                }
            });
        }

        // If no tags were created from pre_check_settings, preserve existing tags
        if (tags.length === 0) {
            tags = [...(booking.tags || [])];
        }

        return tags;
    };

    return {
        ...booking,
        // Map user fields to booking level for Lead Booker consistency
        passport_number: booking.user?.passport_number || booking.passport_number || '',
        emergency_contact: booking.user?.emergency_contact || booking.emergency_contact || '',
        custom_note: booking.user?.custom_note || booking.custom_note || '',
        is_minor: booking.user?.is_minor !== undefined ? booking.user.is_minor : (booking.is_minor || false),
        dietary: booking.user?.dietary || booking.dietary || [],
        allergies: booking.user?.allergies || booking.allergies || [],
        notes: booking.user?.notes || booking.notes || '',
        // Map tags for Lead Booker - create from category fields and existing tags
        tags: createTagsFromCategoryFields(booking),
        // Map party participants
        party: (() => {
            // Map existing party members
            const existingParty = (booking.party || []).map(mapUserFieldsToParticipant);

            // Calculate how many virtual participants we need based on people_count
            // people_count includes lead booker, so extra = people_count - 1 - party.length
            const expectedPartySize = (booking.people_count || 1) - 1;
            const virtualCount = Math.max(0, expectedPartySize - existingParty.length);

            // Create virtual participants with id: null for those without sub-bookings yet
            const virtualParticipants = Array.from({ length: virtualCount }, (_, index) => ({
                id: null, // null id signals this is a new participant without sub-booking
                user: {
                    id: null,
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    lang: 'en'
                },
                tags: [],
                waiver_signatures: [],
                _isVirtual: true, // Internal flag to identify virtual participants
                _virtualIndex: existingParty.length + index // For tracking position
            }));

            return [...existingParty, ...virtualParticipants];
        })(),
        // pick FIRST from these: reseller_ref, supplier_ref, order_ref
        ref: [booking.reseller_ref, booking.supplier_ref, booking.order_ref].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i)[0] || '',
    };
}

const topcode = window.location.host.split('.')[0];

export const ApiContext = React.createContext();

export const ApiContextProvider = ({ children }) => {
    const shctx = sharedContext.get('session');
    const isTokenValid = shctx?.token_expires
        ? dayjs().isBefore(dayjs(shctx.token_expires))
        : false;
    const isSessionValid = !!shctx && isTokenValid;

    // Clear expired session from localStorage immediately
    if (shctx && !isTokenValid) {
        sharedContext.delete('session');
    }

    // Apply alignment when initializing state from sharedContext
    const [booking, setBooking] = React.useState(isSessionValid ? alignBookingData(shctx?.booking) : null);
    const [waivers, setWaivers] = React.useState(isSessionValid && shctx?.waivers);
    const [settings, setSettings] = React.useState(isSessionValid && shctx?.settings);
    const [tourOperator, setTourOperator] = React.useState(isSessionValid && shctx?.tour_operator);
    const [preCheckSettings, setPreCheckSettings] = React.useState(isSessionValid && shctx?.pre_check_settings);
    const [token, setToken] = React.useState(isSessionValid && shctx?.token);
    const [tokenExpires, setTokenExpires] = React.useState(isSessionValid && shctx?.token_expires);
    const [isAuthenticated, setIsAuthenticated] = React.useState(isSessionValid && !!shctx?.token);
    const [loginError, setLoginError] = React.useState(null);

    const [isActive, setIsActive] = React.useState(false);
    // const [sessionSecondsLeft, setSessionSecondsLeft] = React.useState(null);

    // Track current form data for participant status updates
    const [currentFormData, setCurrentFormData] = React.useState(null);
    const [currentParticipantIndex, setCurrentParticipantIndex] = React.useState(null);

    // Track skipped participants (session only)
    const [skippedParticipants, setSkippedParticipants] = React.useState(new Set());

    // Track participants saved in current session
    const [savedInSession, setSavedInSession] = React.useState(new Set());

    // Booking refresh bookkeeping (CP-6549); refs because timers and window listeners read them
    const bookingRef = React.useRef(booking);
    bookingRef.current = booking;
    const formRef = React.useRef({ data: null, index: null });
    formRef.current = { data: currentFormData, index: currentParticipantIndex };
    const writesInFlight = React.useRef(0);
    const writeGeneration = React.useRef(0);
    const lastBookingRefresh = React.useRef(0);

    // A save, skip or pre-check-in answers with the booking as it wrote it: a refresh that
    // overlaps one would be older than that answer.
    const trackWrite = (write) => async (...args) => {
        writesInFlight.current += 1;
        writeGeneration.current += 1;
        try {
            return await write(...args);
        } finally {
            writesInFlight.current -= 1;
            writeGeneration.current += 1;
        }
    };

    // Functions to manage skipped participants
    const setParticipantSkipped = (participantIndex) => {
        setSkippedParticipants(prev => new Set(prev).add(participantIndex));
    };

    const clearParticipantSkipped = (participantIndex) => {
        setSkippedParticipants(prev => {
            const newSet = new Set(prev);
            newSet.delete(participantIndex);
            return newSet;
        });
    };

    const isParticipantSkipped = (participantIndex) => {
        return skippedParticipants.has(participantIndex);
    };

    // Functions to manage saved participants
    const markParticipantAsSaved = (participantIndex) => {
        setSavedInSession(prev => new Set([...prev, participantIndex]));
    };

    const isParticipantSavedInSession = (participantIndex) => {
        return savedInSession.has(participantIndex);
    };

    const refreshInterval = 1000;
    // One expiry timer per token. It used to start in the render body, so every render added an
    // interval that was never cleared, and a stale one could sign out a later session.
    React.useEffect(() => {
        if (!token || !tokenExpires) {
            return undefined;
        }
        let stm = null;
        const doRefreshToken = () => {
            const now = dayjs();
            const exp = dayjs(tokenExpires);
            const diff = exp.diff(now, 'second');
            // setSessionSecondsLeft(diff);
            if (diff <= 0) {
                clearInterval(stm);
                logout();
                window.location.href = '/';
            }
        }
        stm = setInterval(doRefreshToken, refreshInterval);
        return () => clearInterval(stm);
        // logout only calls state setters and sharedContext
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, tokenExpires]);

    const postHttpHeaders = {
        'Content-Type': 'application/json',
    }
    if (isSessionValid && token) {
        postHttpHeaders['Authorization'] = `Token ${shctx.token}`;
    }

    const basePrefix = `/${topcode}/bookingmgmt/`;

    const login = async (booking_ref, last_name) => {
        setIsActive(true);
        try {
            const res = await client.post(`${basePrefix}login/`, { booking_ref, last_name, topcode })

            if (res.error && res.error.status === 404) {
                setLoginError("Booking not found.");
                return false;
            }

            const closedMessage = tenantClosedMessage(res);
            if (closedMessage) {
                setLoginError(closedMessage);
                return false;
            }

            // Rate-limited sign-in attempts (backend throttle): res.data is null for an error
            // response, so the generic branch below would show nothing.
            if (res.status === 429) {
                setLoginError(statusFallbackMessage(429));
                return false;
            }

            // Fixed wording: an error body here is a framework or proxy message, not one for the guest
            if ([200, 201].indexOf(res.status) === -1) {
                setLoginError(res.status >= 500 || res.status === 408
                    ? statusFallbackMessage(res.status)
                    : 'We could not sign you in. Please check your details and try again.');
                return false;
            }

            // A backend crash can answer 200 with its HTML error page, which parses to no data
            if (!res.data?.token) {
                setLoginError(statusFallbackMessage(500));
                return false;
            }

            // Align booking data *before* saving to sharedContext and setting state
            const alignedBooking = alignBookingData(res.data?.booking);
            const sessionData = {
                ...res.data,
                booking: alignedBooking, // Store the aligned booking data
            };
            sharedContext.set('session', sessionData);

            setIsAuthenticated(!!res.data?.token);
            setBooking(alignedBooking); // Set state with aligned data
            setSettings(res.data?.settings);
            setToken(res.data?.token);
            setTokenExpires(res.data?.token_expires);
            setTourOperator(res.data?.tour_operator);
            setWaivers(res.data?.waivers);
            setPreCheckSettings(res.data?.pre_check_settings);
            setLoginError(null); // Clear previous login errors
            lastBookingRefresh.current = Date.now(); // just read it
            return true;

        } catch (error) {
            console.error('mgmt: login: error:', error);
            setLoginError("Service is temporarily unavailable. Please try again later.");
            return false;
        } finally {
            setIsActive(false);
        }
    }

    // Signed-in calls: a closed tenant ends the session -- the stored session is cleared, the sign-in page shows why
    // (loginError) -- and the call fails with the same words. The sign-in page is the root route only.
    const refuseIfTenantClosed = (res) => {
        const message = tenantClosedMessage(res);
        if (!message) return;
        logout();
        setLoginError(message);
        if (window.location.pathname !== '/') {
            window.history.replaceState(null, '', '/');
        }
        const error = new Error(message);
        error.tenantClosed = true; // signed out: the callers' catch must not restore the booking
        throw error;
    };

    const refreshToken = async () => {
        setIsActive(true);
        try {
            const res = await client.post(`${basePrefix}refresh_token/`, {}, { headers: postHttpHeaders })
            if ([200, 201].indexOf(res.status) === -1) {
                return false;
            }

            setToken(res.data?.token);
            setTokenExpires(res.data?.token_expires);
            return true;

        } catch (error) {
            return false;
        } finally {
            setIsActive(false);
        }
    }

    const saveParticipant = trackWrite(async (payload) => {
        setIsActive(true);
        const originalBooking = booking;
        let alignedOptimisticBooking = null;

        try {
            const optimisticBooking = applySaveParticipantOptimisticUpdate(originalBooking, payload);
            alignedOptimisticBooking = alignBookingData(optimisticBooking);

            setBooking(alignedOptimisticBooking);
            const currentSession = sharedContext.get('session');
            if (currentSession) {
                sharedContext.set('session', {
                    ...currentSession,
                    booking: alignedOptimisticBooking,
                });
            }
        } catch (error) {
            console.error('Optimistic update failed:', error);
            setIsActive(false);
            return Promise.reject(error);
        }

        try {
            // Use the same endpoint as checkin but just save the data without changing status
            const res = await client.post(`${basePrefix}checkin/`, payload, { headers: postHttpHeaders })
            refuseIfTenantClosed(res);

            if ([200, 201].indexOf(res.status) >= 0) {
                let alignedActualBooking = res.data ? alignBookingData(res.data) : alignedOptimisticBooking;

                // Preserve tag category fields from optimistic update if they're missing from server response
                if (alignedActualBooking && alignedOptimisticBooking && originalBooking.id === payload.booking_id) {
                    Object.keys(alignedOptimisticBooking).forEach((key) => {
                        if (['id', 'user', 'party', 'tags', 'waiver_signatures', 'pre_check_settings'].includes(key)) {
                            return;
                        }
                        if (alignedActualBooking[key] === undefined && alignedOptimisticBooking[key] !== undefined) {
                            alignedActualBooking[key] = alignedOptimisticBooking[key];
                        }
                    });
                }

                if (alignedActualBooking) {
                    setBooking(alignedActualBooking);
                    const currentSession = sharedContext.get('session');
                    if (currentSession) {
                        sharedContext.set('session', {
                            ...currentSession,
                            booking: alignedActualBooking,
                        });
                    }
                    return alignedActualBooking;
                } else {
                    console.warn('Save successful, but backend did not return valid booking data. Keeping optimistic state.');
                    return alignedOptimisticBooking;
                }
            } else {
                console.error('Save failed on backend:', res.error?.data ?? res.data, res.status);
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }

                if ([400, 406].includes(res.status)) {
                    const validationError = new Error(userFacingApiError(res, 'Could not save your changes. Please check the form.'));
                    validationError.status = res.status;
                    // {error, errors: {field id: message}}: each message is shown under its field
                    const fieldErrors = fieldErrorsFromResponse(res.error?.data);
                    if (fieldErrors) {
                        validationError.fieldErrors = fieldErrors;
                    }
                    throw validationError;
                }

                throw new Error(userFacingApiError(res, 'Could not save your changes. Please try again.'));
            }
        } catch (error) {
            console.error('mgmt: save: error/rollback:', error);
            if (!error?.tenantClosed && alignedOptimisticBooking && booking !== originalBooking) {
                console.warn('Rolling back optimistic update due to error...');
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
            }
            throw error;
        } finally {
            setIsActive(false);
        }
    });

    const skipParticipant = trackWrite(async (payload) => {
        setIsActive(true);
        const originalBooking = booking;
        let alignedOptimisticBooking = null;

        const bookingId = payload?.booking_id;
        const virtualIndex = payload?._virtualIndex;

        // For virtual participants without sub-booking, booking_id will be null
        // In this case, we identify them by _virtualIndex
        const isVirtualParticipant = bookingId === null && virtualIndex !== undefined;

        if (!bookingId && !isVirtualParticipant) {
            setIsActive(false);
            return Promise.reject(new Error('booking_id is required to skip participant'));
        }

        try {
            // Create optimistic update
            let optimisticBooking = { ...originalBooking };

            // Determine if this is lead booker or party member
            const isLeadBooker = originalBooking.id === bookingId;

            if (isLeadBooker) {
                // Set check_in_skipped for lead booker
                optimisticBooking.check_in_skipped = true;
            } else {
                // Find and update party member (including virtual participants)
                const updatedParty = originalBooking.party.map(p => {
                    // For virtual participants, match by _virtualIndex
                    const isMatch = isVirtualParticipant
                        ? (p.id === null && p._virtualIndex === virtualIndex)
                        : (p.id === bookingId);

                    if (isMatch) {
                        return {
                            ...p,
                            check_in_skipped: true
                        };
                    }
                    return p;
                });
                optimisticBooking.party = updatedParty;
            }

            alignedOptimisticBooking = alignBookingData(optimisticBooking);

            setBooking(alignedOptimisticBooking);
            const currentSession = sharedContext.get('session');
            if (currentSession) {
                sharedContext.set('session', {
                    ...currentSession,
                    booking: alignedOptimisticBooking,
                });
            }
        } catch (error) {
            console.error('Optimistic update failed:', error);
            setIsActive(false);
            return Promise.reject(error);
        }

        try {
            const requestPayload = {
                ...payload,
                check_in_skipped: true
            };

            const res = await client.post(`${basePrefix}checkin/`, requestPayload, { headers: postHttpHeaders });
            refuseIfTenantClosed(res);

            if ([200, 201].indexOf(res.status) >= 0) {
                let alignedActualBooking = res.data ? alignBookingData(res.data) : alignedOptimisticBooking;

                if (alignedActualBooking) {
                    setBooking(alignedActualBooking);
                    const currentSession = sharedContext.get('session');
                    if (currentSession) {
                        sharedContext.set('session', {
                            ...currentSession,
                            booking: alignedActualBooking,
                        });
                    }
                    return alignedActualBooking;
                } else {
                    console.warn('Skip successful, but backend did not return valid booking data. Keeping optimistic state.');
                    return alignedOptimisticBooking;
                }
            } else {
                console.error('Skip failed on backend:', res.error?.data ?? res.data, res.status);
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
                throw new Error(userFacingApiError(res, 'Could not skip this step. Please try again.'));
            }
        } catch (error) {
            console.error('mgmt: skip: error/rollback:', error);
            if (!error?.tenantClosed && alignedOptimisticBooking && booking !== originalBooking) {
                console.warn('Rolling back optimistic update due to error...');
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
            }
            throw error;
        } finally {
            setIsActive(false);
        }
    });

    const checkInParticipant = trackWrite(async (payload) => {
        setIsActive(true);
        const originalBooking = booking;
        let alignedOptimisticBooking = null;

        try {
            let optimisticBooking = { ...originalBooking };

            // Check if this is a Lead Booker update (booking.user.id)
            if (originalBooking.user?.id === payload.user_id) {
                // Update Lead Booker (booking.user)
                const updatedUser = {
                    ...originalBooking.user,
                    status: checkedInStatus,
                };

                // Update user fields dynamically
                const userFields = ['first_name', 'last_name', 'email', 'phone'];
                userFields.forEach(field => {
                    if (payload[field] !== undefined) {
                        updatedUser[field] = payload[field];
                    }
                });

                // Update participant-like fields on user
                const participantFields = [
                    'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                    'dietary', 'allergies', 'notes'
                ];
                participantFields.forEach(field => {
                    if (payload[field] !== undefined) {
                        updatedUser[field] = payload[field];
                    }
                });

                // Handle any additional custom fields (including tag category fields)
                Object.keys(payload).forEach(key => {
                    if (!['participant_id', 'user_id', 'first_name', 'last_name', 'email', 'phone',
                        'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                        'dietary', 'allergies', 'notes'].includes(key)) {
                        updatedUser[key] = payload[key];
                    }
                });

                optimisticBooking.user = updatedUser;

                // For Lead Booker, also update booking-level fields (including tags)
                const bookingFields = [
                    'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                    'dietary', 'allergies', 'notes'
                ];
                bookingFields.forEach(field => {
                    if (payload[field] !== undefined) {
                        optimisticBooking[field] = payload[field];
                    }
                });

                // Handle tag category fields at booking level for Lead Booker
                Object.keys(payload).forEach(key => {
                    if (!['participant_id', 'user_id', 'first_name', 'last_name', 'email', 'phone',
                        'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                        'dietary', 'allergies', 'notes'].includes(key)) {
                        // For Lead Booker, these fields should be at booking level
                        optimisticBooking[key] = payload[key];
                    }
                });
            } else {
                // Update participant from party
                const updatedParty = originalBooking.party.map(p => {
                    if (p.id === payload.participant_id) {
                        const updatedParticipant = {
                            ...p,
                            status: checkedInStatus,
                        };

                        // Update user fields dynamically
                        const userFields = ['first_name', 'last_name', 'email', 'phone'];
                        const userParticipantFields = ['passport_number', 'emergency_contact', 'custom_note', 'is_minor', 'dietary', 'allergies', 'notes'];
                        const hasUserUpdates = [...userFields, ...userParticipantFields].some(field => payload[field] !== undefined);

                        if (hasUserUpdates) {
                            updatedParticipant.user = {
                                ...p.user,
                            };

                            // Update basic user fields
                            userFields.forEach(field => {
                                if (payload[field] !== undefined) {
                                    updatedParticipant.user[field] = payload[field];
                                }
                            });

                            // Update participant-like fields in user object
                            userParticipantFields.forEach(field => {
                                if (payload[field] !== undefined) {
                                    updatedParticipant.user[field] = payload[field];
                                }
                            });
                        }

                        // Update participant fields dynamically (for both user and participant level)
                        const participantFields = [
                            'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                            'dietary', 'allergies', 'notes'
                        ];
                        participantFields.forEach(field => {
                            if (payload[field] !== undefined) {
                                updatedParticipant[field] = payload[field];
                            }
                        });

                        // Handle any additional custom fields
                        Object.keys(payload).forEach(key => {
                            if (!['participant_id', 'first_name', 'last_name', 'email', 'phone',
                                'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                                'dietary', 'allergies', 'notes'].includes(key)) {
                                updatedParticipant[key] = payload[key];
                            }
                        });

                        return updatedParticipant;
                    }
                    return p;
                });

                optimisticBooking.party = updatedParty;
            }

            alignedOptimisticBooking = alignBookingData(optimisticBooking);

            setBooking(alignedOptimisticBooking);
            const currentSession = sharedContext.get('session');
            if (currentSession) {
                sharedContext.set('session', {
                    ...currentSession,
                    booking: alignedOptimisticBooking,
                });
            }
        } catch (error) {
            console.error('Optimistic update failed:', error);
            setIsActive(false);
            return Promise.reject(error);
        }

        try {
            const res = await client.post(`${basePrefix}checkin/`, payload, { headers: postHttpHeaders })
            refuseIfTenantClosed(res);

            if ([200, 201].indexOf(res.status) >= 0) {

                let alignedActualBooking = res.data ? alignBookingData(res.data) : alignedOptimisticBooking;

                // Preserve tag category fields from optimistic update if they're missing from server response
                if (alignedActualBooking && alignedOptimisticBooking && originalBooking.user?.id === payload.user_id) {
                    // For Lead Booker, preserve tag category fields that might not be returned by server
                    Object.keys(payload).forEach(key => {
                        if (!['user_id', 'participant_id', 'first_name', 'last_name', 'email', 'phone',
                            'passport_number', 'emergency_contact', 'custom_note', 'is_minor',
                            'dietary', 'allergies', 'notes'].includes(key)) {
                            // If server didn't return this field, use the optimistic value
                            if (alignedActualBooking[key] === undefined && alignedOptimisticBooking[key] !== undefined) {
                                alignedActualBooking[key] = alignedOptimisticBooking[key];
                            }
                        }
                    });
                }

                if (alignedActualBooking) {
                    setBooking(alignedActualBooking);
                    const currentSession = sharedContext.get('session');
                    if (currentSession) {
                        sharedContext.set('session', {
                            ...currentSession,
                            booking: alignedActualBooking,
                        });
                    }
                    return alignedActualBooking;
                } else {
                    console.warn('Pre-check-in successful, but backend did not return valid booking data. Keeping optimistic state.');
                    return alignedOptimisticBooking;
                }
            } else {
                console.error('Pre-check-in failed on backend:', res.error?.data ?? res.data, res.status);
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
                throw new Error(userFacingApiError(res, 'Pre-check-in could not be completed. Please try again.'));
            }
        } catch (error) {
            console.error('mgmt: preCheckin: error/rollback:', error);
            if (!error?.tenantClosed && alignedOptimisticBooking && booking !== originalBooking) {
                console.warn('Rolling back optimistic update due to error...');
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
            }
            throw error;
        } finally {
            setIsActive(false);
        }
    });

    const preCheckin = trackWrite(async (payload) => {
        setIsActive(true);
        const originalBooking = booking;
        let alignedOptimisticBooking = null;

        try {
            const isMinimalUpdate = !payload?.bookings_to_check_in;

            if (isMinimalUpdate) {
                const res = await client.post(`${basePrefix}checkin/signatures/`, payload, { headers: postHttpHeaders })
                refuseIfTenantClosed(res);

                if ([200, 201].indexOf(res.status) >= 0) {
                    const updatedBooking = alignBookingData({
                        ...originalBooking,
                        user: {
                            ...originalBooking.user,
                            allow_ads: payload?.user?.allow_ads,
                        },
                    });

                    setBooking(updatedBooking);
                    const currentSession = sharedContext.get('session');
                    if (currentSession) {
                        sharedContext.set('session', {
                            ...currentSession,
                            booking: updatedBooking,
                        });
                    }

                    return res.data;
                } else {
                    throw new Error(userFacingApiError(res, 'Could not save signatures. Please try again.'));
                }
            }

            // Optimistic update: set checked_in to true for all participants being pre-checked-in
            let optimisticBooking = {
                ...originalBooking,
                user: {
                    ...originalBooking.user,
                    allow_ads: payload?.user?.allow_ads,
                }
            };

            // Update Lead Booker checked_in status if included in bookings_to_check_in
            if (Array.isArray(payload.bookings_to_check_in) && payload.bookings_to_check_in.includes(originalBooking.id)) {
                optimisticBooking.checked_in = true;
            }

            // Update party members checked_in status if included in bookings_to_check_in
            if (optimisticBooking.party) {
                optimisticBooking.party = optimisticBooking.party.map(participant => {
                    if (Array.isArray(payload.bookings_to_check_in) && payload.bookings_to_check_in.includes(participant.id)) {
                        return { ...participant, checked_in: true };
                    }
                    return participant;
                });
            }

            alignedOptimisticBooking = alignBookingData(optimisticBooking);

            // Set optimistic state
            setBooking(alignedOptimisticBooking);
            const currentSession = sharedContext.get('session');
            if (currentSession) {
                sharedContext.set('session', {
                    ...currentSession,
                    booking: alignedOptimisticBooking,
                });
            }

            const res = await client.post(`${basePrefix}checkin/signatures/`, payload, { headers: postHttpHeaders })
            refuseIfTenantClosed(res);

            if ([200, 201].indexOf(res.status) >= 0) {
                // Process the response to update checked_in status based on processed_participants
                const processedParticipants = res.data?.processed_participants || [];

                console.debug("Processing preCheckin response:", {
                    processedParticipants,
                    originalBookingId: originalBooking.id,
                    originalUserId: originalBooking.user?.id,
                    originalPartyIds: originalBooking.party?.map(p => p.id) || []
                });

                let updatedBooking = {
                    ...alignedOptimisticBooking,
                    user: {
                        ...alignedOptimisticBooking.user,
                        allow_ads: payload?.user?.allow_ads,
                    }
                };

                // Update checked_in status for participants in the response
                processedParticipants.forEach(processedParticipant => {
                    if (processedParticipant.user_id) {
                        // This is the Lead Booker
                        if (processedParticipant.user_id === updatedBooking.user?.id) {
                            console.debug(`Setting Lead Booker (user_id: ${processedParticipant.user_id}) checked_in to true`);
                            updatedBooking.checked_in = true;
                        }
                    } else if (processedParticipant.participant_id) {
                        // This is a party member
                        if (updatedBooking.party) {
                            updatedBooking.party = updatedBooking.party.map(participant => {
                                if (participant.id === processedParticipant.participant_id) {
                                    console.debug(`Setting party member (participant_id: ${processedParticipant.participant_id}) checked_in to true`);
                                    return { ...participant, checked_in: true };
                                }
                                return participant;
                            });
                        }
                    }
                });

                const finalAlignedBooking = alignBookingData(updatedBooking);

                console.debug("Final booking state after preCheckin:", {
                    leadBookerCheckedIn: finalAlignedBooking.checked_in,
                    partyMembersCheckedIn: finalAlignedBooking.party?.map(p => ({ id: p.id, checked_in: p.checked_in })) || []
                });

                // Update state with the processed response
                setBooking(finalAlignedBooking);
                const updatedSession = sharedContext.get('session');
                if (updatedSession) {
                    sharedContext.set('session', {
                        ...updatedSession,
                        booking: finalAlignedBooking,
                    });
                }

                return res.data;
            } else {
                console.error('Checkin failed on backend:', res.error?.data ?? res.data, res.status);
                // Rollback optimistic update on error
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
                throw new Error(userFacingApiError(res, 'Pre-check-in could not be completed. Please try again.'));
            }
        } catch (error) {
            console.error('mgmt: preCheckin: error:', error);
            // Rollback optimistic update on error
            if (!error?.tenantClosed && alignedOptimisticBooking && booking !== originalBooking) {
                console.warn('Rolling back optimistic update due to error...');
                setBooking(originalBooking);
                const currentSession = sharedContext.get('session');
                if (currentSession) {
                    sharedContext.set('session', {
                        ...currentSession,
                        booking: originalBooking,
                    });
                }
            }
            throw error;
        } finally {
            setIsActive(false);
        }
    });

    const logout = () => {
        sharedContext.delete('session');
        setIsAuthenticated(false);
        setBooking(null);
        setSettings(null);
        setToken(null);
        setTokenExpires(null);
        setTourOperator(null);
        setWaivers(null);
        setPreCheckSettings(null);
        setCurrentFormData(null);
        setCurrentParticipantIndex(null);
        setSkippedParticipants(new Set());
        setSavedInSession(new Set());
    }

    // Functions to manage current form state
    const updateCurrentFormData = (formData, participantIndex) => {
        setCurrentFormData(formData);
        setCurrentParticipantIndex(participantIndex);
    };

    const clearCurrentFormData = () => {
        setCurrentFormData(null);
        setCurrentParticipantIndex(null);
    };

    // Re-read the signed-in booking (CP-6549). Quiet on any failure: the session keeps its copy.
    const refreshBooking = async () => {
        const session = sharedContext.get('session');
        const now = Date.now();
        if (!session?.token || writesInFlight.current > 0 || now - lastBookingRefresh.current < BOOKING_REFRESH_MIN_GAP_MS) {
            return;
        }
        lastBookingRefresh.current = now;
        const generation = writeGeneration.current;
        try {
            const res = await client.get(`${basePrefix}booking/`, { headers: { Authorization: `Token ${session.token}` } });
            if (res.status !== 200 || !res.data?.booking) {
                return;
            }
            // A save, skip or pre-check-in ran meanwhile: its answer is newer than this read
            if (generation !== writeGeneration.current) {
                return;
            }
            // Signed out, or in again, meanwhile
            const currentSession = sharedContext.get('session');
            if (!currentSession || currentSession.token !== session.token) {
                return;
            }

            const current = bookingRef.current;
            const fresh = alignBookingData(res.data.booking);
            if (!current || JSON.stringify(fresh) === JSON.stringify(current)) {
                return;
            }

            // The open form re-initializes when its participant changes (a guest-app join fills
            // the virtual slot being edited): keep this copy rather than discard what was typed.
            const { data, index } = formRef.current;
            if (index !== null && index !== undefined && !isBlankForm(data)) {
                const before = getParticipantByIndex(current, index);
                const after = getParticipantByIndex(fresh, index);
                if ((before?.id ?? null) !== (after?.id ?? null)) {
                    return;
                }
            }

            setBooking(fresh);
            sharedContext.set('session', { ...currentSession, booking: fresh });
        } catch (error) {
            console.warn('mgmt: refreshBooking: keeping the current booking:', error);
        }
    };

    React.useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }
        const refreshIfVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshBooking();
            }
        };
        refreshIfVisible(); // page load with a stored session; right after sign-in it is a no-op
        const timer = setInterval(refreshIfVisible, BOOKING_REFRESH_INTERVAL_MS);
        document.addEventListener('visibilitychange', refreshIfVisible);
        window.addEventListener('focus', refreshIfVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', refreshIfVisible);
            window.removeEventListener('focus', refreshIfVisible);
        };
        // refreshBooking reads everything that changes through refs and sharedContext
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    return (<ApiContext.Provider value={{
        isAuthenticated,
        client,
        login,
        loginError,
        logout,
        saveParticipant,
        skipParticipant,
        checkInParticipant,
        preCheckin,
        isActive,
        booking,
        waivers,
        settings,
        tourOperator,
        token,
        tokenExpires,
        preCheckSettings,
        // sessionSecondsLeft,
        // Current form state for status tracking
        currentFormData,
        currentParticipantIndex,
        updateCurrentFormData,
        clearCurrentFormData,
        // Skipped participants management
        skippedParticipants,
        setParticipantSkipped,
        clearParticipantSkipped,
        isParticipantSkipped,
        // Saved participants management
        savedInSession,
        markParticipantAsSaved,
        isParticipantSavedInSession,
    }}>
        {children}
    </ApiContext.Provider>);
}
